import { z } from "zod";
import { OrganizationHomeImageResDto } from "service/api/estabrookSchemas";
import { countCharacters } from "@/shared/utils/character-count";
import {
  HOME_SUBTITLE_MAX_LENGTH,
  HOME_TITLE_MAX_LENGTH,
} from "../constants/promotional-banner";
import {
  AddOrganizationFormValues,
  EditOrganizationFormValues,
  PromotionalBannerFormValues,
} from "../types/organization-form";

// Formik turns "" into undefined before validating, so the text fields are
// optional here - the headline's "required" rule lives in the refinement.
export const promotionalBannerSchema = z.object({
  showPromotionalBanner: z.boolean(),
  homeTitle: z.string().optional(),
  homeSubtitle: z.string().optional(),
  homeImageFile: z.custom<File | null>(),
  removeHomeImage: z.boolean(),
});

// Headline/subline rules only apply while the banner is shown - a hidden
// banner keeps whatever was saved before and isn't validated.
export const refinePromotionalBanner = (
  values: z.infer<typeof promotionalBannerSchema>,
  ctx: z.RefinementCtx,
) => {
  if (!values.showPromotionalBanner) return;

  const homeTitle = values.homeTitle ?? "";
  const homeSubtitle = values.homeSubtitle ?? "";

  if (!homeTitle.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["homeTitle"],
      message: "Headline is required",
    });
  } else if (countCharacters(homeTitle) > HOME_TITLE_MAX_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["homeTitle"],
      message: `Headline can be at most ${HOME_TITLE_MAX_LENGTH} characters`,
    });
  }

  if (countCharacters(homeSubtitle) > HOME_SUBTITLE_MAX_LENGTH) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["homeSubtitle"],
      message: `Subline can be at most ${HOME_SUBTITLE_MAX_LENGTH} characters`,
    });
  }
};

// Combine with the base form schema via z.intersection, not .merge():
// .merge().superRefine() skips the refinement whenever a base field (e.g. an
// empty Name) fails, so "Headline is required" wouldn't show until the rest
// of the form is filled in.
export const promotionalBannerValidationSchema =
  promotionalBannerSchema.superRefine(refinePromotionalBanner);

export const getPromotionalBannerInitialValues = (
  homeImage?: OrganizationHomeImageResDto | null,
): PromotionalBannerFormValues => ({
  showPromotionalBanner: homeImage?.enabled ?? false,
  homeTitle: homeImage?.title ?? "",
  homeSubtitle: homeImage?.subtitle ?? "",
  homeImageFile: null,
  removeHomeImage: false,
});

export const getHomeImageDataUrl = (
  homeImage?: OrganizationHomeImageResDto | null,
) => {
  if (!homeImage?.data || !homeImage.contentType) return null;
  // contentType is typed as an object by the generator, but it's the MIME
  // type string (e.g. "image/jpeg").
  return `data:${String(homeImage.contentType)};base64,${homeImage.data}`;
};

type MultipartValue = string | boolean | Blob | null | undefined;

// Both organization endpoints are multipart/form-data. Undefined/null fields
// are left out ("unchanged" on PATCH); booleans are sent as "true"/"false".
const toFormData = (fields: Record<string, MultipartValue>) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, value instanceof Blob ? value : String(value));
  });
  return formData;
};

export const buildCreateOrganizationFormData = ({
  name,
  state,
  address,
  city,
  zipCode,
  replyToEmail,
  showPromotionalBanner,
  homeTitle,
  homeSubtitle,
  homeImageFile,
}: AddOrganizationFormValues) =>
  toFormData({
    name,
    state,
    address,
    // city/zipCode/replyToEmail are optional on the backend, but an explicit
    // "" fails its @IsNotEmpty() check - leave blank ones out instead.
    city: city || undefined,
    zipCode: zipCode || undefined,
    replyToEmail: replyToEmail || undefined,
    ...(showPromotionalBanner && {
      showPromotionalBanner: true,
      homeTitle: homeTitle.trim(),
      homeSubtitle: homeSubtitle.trim() || undefined,
      file: homeImageFile,
    }),
  });

export const buildUpdateOrganizationFormData = ({
  showPromotionalBanner,
  homeTitle,
  homeSubtitle,
  homeImageFile,
  removeHomeImage,
  replyToEmail,
  ...organization
}: EditOrganizationFormValues) =>
  toFormData({
    // On PATCH an empty string clears city/zipCode.
    ...organization,
    // The API's @IsEmail() only allows null, which multipart can't send, so
    // "" fails every save. Leave a blank one out ("unchanged") instead.
    // Clearing a saved reply-to needs the API to map "" to null.
    replyToEmail: replyToEmail || undefined,
    showPromotionalBanner,
    // While the banner is hidden, leave the saved headline/subline/image
    // untouched so turning it back on restores them.
    ...(showPromotionalBanner && {
      homeTitle: homeTitle.trim(),
      // "" clears a previously saved subline.
      homeSubtitle: homeSubtitle.trim(),
      file: homeImageFile,
      // The backend rejects removeHomeImage together with a new file; a new
      // file replaces the saved image anyway.
      removeHomeImage: !homeImageFile && removeHomeImage ? true : undefined,
    }),
  });

// The backend reports banner image problems (format, size, 3:1 ratio, ...)
// through the same error shape as any other validation failure. Pull those
// out so the drop zone can show them next to the image, not just in a toast.
export const getHomeImageErrorMessage = (error: unknown): string | null => {
  const errors = (error as { errors?: { code?: string; detail?: string }[] })
    ?.errors;
  const imageError = errors?.find(({ code, detail }) =>
    /image|file/i.test(`${code ?? ""} ${detail ?? ""}`),
  );
  return imageError?.detail ?? null;
};

export const getErrorDetail = (error: unknown): string | null =>
  (error as { errors?: { detail?: string }[] })?.errors?.[0]?.detail ?? null;
